import { useState } from "react"
import { useTranslation } from "react-i18next"
import { formatDate } from "../../utils/date"
import StatusBadge from "../StatusBadge"

const IconButton = ({ title, onClick, disabled, children, tone = "default" }) => {
  const toneClasses =
    tone === "danger"
      ? "border-[#FCE4E4] text-[#EF4444] hover:bg-[#FEF2F2]"
      : "border-[#E7EAEF] text-[#1F2937] hover:bg-[#F4F6F9]"

  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded-full border bg-white transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed ${toneClasses}`}
    >
      {children}
    </button>
  )
}

const UserRow = ({ user, onGenerate, onRevoke, onViewDetails }) => {
  const { t } = useTranslation()
  const [generating, setGenerating] = useState(false)

  const activeTokens = user.active_tokens || []
  const activeCount = user.active_tokens_count ?? activeTokens.length
  const maxTokens = user.max_tokens ?? 5
  const atLimit = activeCount >= maxTokens
  const lastUsedAt = activeTokens.reduce((latest, tk) => {
    if (!tk.last_used_at) return latest
    if (!latest || new Date(tk.last_used_at) > new Date(latest)) return tk.last_used_at
    return latest
  }, null)

  const handleGenerate = async (e) => {
    e.stopPropagation()
    setGenerating(true)
    try {
      await onGenerate(user)
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 hover:bg-[#FAFBFC] cursor-pointer"
      onClick={() => onViewDetails(user)}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1F2937] truncate">{user.name || user.email}</div>
        <div className="text-xs text-[#9AA5B4] truncate">
          {user.email}
          {user.company_name ? ` · ${user.company_name}` : ""}
        </div>
      </div>

      <div className="hidden md:block w-40 shrink-0">
        <StatusBadge count={activeCount} max={maxTokens} />
        {lastUsedAt && (
          <div className="text-[11px] text-[#9AA5B4] mt-1">
            {t("admin_extension_tokens.row.last_used")}: {formatDate(lastUsedAt)}
          </div>
        )}
      </div>

      <div className="hidden sm:block w-28 shrink-0 text-right">
        <div className="text-sm font-medium text-[#1F2937]">{user.imports_total}</div>
        <div className="text-[11px] text-[#9AA5B4]">
          {t("admin_extension_tokens.row.imports_total")}
        </div>
      </div>

      <div className="hidden lg:block w-28 shrink-0 text-right">
        <div className="text-sm font-medium text-[#1F2937]">{user.imports_last_30_days}</div>
        <div className="text-[11px] text-[#9AA5B4]">
          {t("admin_extension_tokens.row.imports_30d")}
        </div>
      </div>

      <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        <IconButton
          title={
            atLimit
              ? t("admin_extension_tokens.row.limit_reached_hint", { max: maxTokens })
              : t("admin_extension_tokens.row.generate")
          }
          onClick={handleGenerate}
          disabled={generating || atLimit}
        >
          ⟳
        </IconButton>
        <IconButton
          title={t("admin_extension_tokens.row.revoke")}
          onClick={() => onRevoke(user)}
          disabled={activeCount === 0}
          tone="danger"
        >
          ⦸
        </IconButton>
      </div>
    </div>
  )
}

export default UserRow
